import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Active Filter Chip — Figma node 166:274 family (Component 01).
 *
 * Pill displaying an active filter with a circular remove button on the
 * right. Used in toolbars above tables. 4 color variants map to the most
 * common filter contexts (channel, region, status, date).
 *
 * Spec: height 26px, padding 4px 6px 4px 12px, r-pill, x button 10px circle.
 */
const filterChipVariants = cva(
  'inline-flex items-center gap-[6px] justify-center rounded-ggc-pill border pl-[12px] pr-[6px] py-[4px] font-ggc ggc-text-micro-bold whitespace-nowrap',
  {
    variants: {
      tone: {
        red: 'bg-ggc-red-light border-ggc-red text-ggc-red',
        blue: 'bg-ggc-blue-light border-ggc-blue text-ggc-blue',
        green: 'bg-ggc-green-light border-ggc-green text-ggc-green',
        purple: 'bg-ggc-purple-light border-ggc-purple text-ggc-purple',
      },
    },
    defaultVariants: {
      tone: 'blue',
    },
  }
);

const iconColorMap: Record<NonNullable<GgcActiveFilterChipProps['tone']>, string> = {
  red: 'bg-ggc-red',
  blue: 'bg-ggc-blue',
  green: 'bg-ggc-green',
  purple: 'bg-ggc-purple',
};

export interface GgcActiveFilterChipProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'onClick'>,
    VariantProps<typeof filterChipVariants> {
  /** Filter label, e.g. "YouTube", "US, DE". */
  label: React.ReactNode;
  /** Click handler for the remove button. */
  onRemove?: () => void;
  /** aria-label for the remove button. Default `"Remove filter"`. */
  removeLabel?: string;
}

const GgcActiveFilterChip = React.forwardRef<HTMLSpanElement, GgcActiveFilterChipProps>(
  ({ className, tone, label, onRemove, removeLabel, ...props }, ref) => {
    const resolvedTone = tone ?? 'blue';
    return (
      <span ref={ref} className={cn(filterChipVariants({ tone: resolvedTone }), className)} {...props}>
        {label}
        <button
          type="button"
          aria-label={removeLabel ?? 'Remove filter'}
          onClick={onRemove}
          className={cn(
            'flex size-[10px] items-center justify-center rounded-full text-white text-[8px] leading-none',
            iconColorMap[resolvedTone]
          )}
        >
          ×
        </button>
      </span>
    );
  }
);
GgcActiveFilterChip.displayName = 'GgcActiveFilterChip';

export { GgcActiveFilterChip, filterChipVariants };
